import { Alert, Box, Button, Card, CardContent, Container, Stack, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import ConfirmDialog from "components/Modals/ConfirmDialog";
import DocumentPreviewDialog from "views/admin/Clients/documents/DocumentPreviewDialog";
import { saveBlob } from "views/admin/Clients/documents/clientDocuments.utils";
import OtpGate from "./OtpGate";
import UploadItemCard from "./UploadItemCard";
import {
  fetchFileContent,
  fetchLanding,
  fetchSession,
  isDeadLink,
  isSessionRejected,
  portalErrorMessage,
  removeFile,
  sendOtp,
  submitRequest,
  uploadFile,
  verifyOtp,
} from "./portal.api";

// sessionStorage, never localStorage: the session dies with the tab, so a
// borrowed device does not carry it to the next person, but an accidental
// reload does not force a fresh code either. Asking for a new code costs the
// client up to the 60 second server-side resend cooldown.
const sessionKey = (token) => `document-upload-session:${token}`;

const readStoredSession = (token) => {
  try {
    return sessionStorage.getItem(sessionKey(token)) || "";
  } catch {
    return "";
  }
};

const DocumentUploadPortal = () => {
  const { token } = useParams();
  const [landing, setLanding] = useState(null);
  const [deadLink, setDeadLink] = useState(false);
  const [loading, setLoading] = useState(true);
  const [accessToken, setAccessToken] = useState(() => readStoredSession(token));
  const [context, setContext] = useState(null);
  const [gateError, setGateError] = useState("");
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [codeRequested, setCodeRequested] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [preview, setPreview] = useState(null);

  const storeSession = useCallback(
    (value) => {
      setAccessToken(value);
      try {
        if (value) sessionStorage.setItem(sessionKey(token), value);
        else sessionStorage.removeItem(sessionKey(token));
      } catch {
        // A browser that refuses storage still works, it just cannot resume.
      }
    },
    [token]
  );

  const dropSession = useCallback(
    (message) => {
      storeSession("");
      setContext(null);
      setGateError(message);
    },
    [storeSession]
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchLanding(token)
      .then((data) => {
        if (cancelled) return;
        setLanding(data);
        setCodeRequested(Boolean(data?.otpSent));
        setCooldownSeconds(data?.retryAfterSeconds ?? 0);
      })
      .catch((err) => {
        if (cancelled) return;
        if (isDeadLink(err)) setDeadLink(true);
        else setGateError(portalErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const loadSession = useCallback(async () => {
    if (!accessToken) return;
    try {
      setContext(await fetchSession(token, accessToken));
    } catch (err) {
      if (isSessionRejected(err)) {
        dropSession("Your session expired. Please verify with a new code.");
      } else if (isDeadLink(err)) {
        setDeadLink(true);
      } else {
        toast.error(portalErrorMessage(err));
      }
    }
  }, [accessToken, dropSession, token]);

  useEffect(() => {
    // Resuming from a stored session must not run once the request has been
    // submitted: the backend closes the request, so the session endpoint 404s.
    if (accessToken && !submitted) loadSession();
  }, [accessToken, loadSession, submitted]);

  const handleSendCode = async () => {
    setSending(true);
    setGateError("");
    try {
      const { retryAfterSeconds } = await sendOtp(token);
      setCodeRequested(true);
      setCooldownSeconds(retryAfterSeconds ?? 0);
      toast.success("Code sent. Check your email.");
    } catch (err) {
      if (isDeadLink(err)) setDeadLink(true);
      else setGateError(portalErrorMessage(err));
    } finally {
      setSending(false);
    }
  };

  const handleVerify = async (code) => {
    setVerifying(true);
    setGateError("");
    try {
      const { accessToken: issued } = await verifyOtp(token, code);
      storeSession(issued);
    } catch (err) {
      if (isDeadLink(err)) setDeadLink(true);
      else setGateError(portalErrorMessage(err));
    } finally {
      setVerifying(false);
    }
  };

  const runSessionAction = async (action) => {
    setBusy(true);
    try {
      await action();
      await loadSession();
    } catch (err) {
      if (isSessionRejected(err)) {
        dropSession("Your session expired. Please verify with a new code.");
      } else {
        // Upload and remove also answer 404 for a stale item or file id, so a
        // 404 here is not proof the link died. Resyncing settles it: the
        // session endpoint 404s only once the request itself is closed.
        toast.error(portalErrorMessage(err));
        await loadSession();
      }
    } finally {
      setBusy(false);
    }
  };

  const handleUpload = (item, file) =>
    runSessionAction(() => uploadFile(token, accessToken, item.id, file));

  const handlePreview = async (file) => {
    // The preview dialog reads the shape staff documents use.
    const document = { ...file, originalFilename: file.filename };
    setPreview({ document, loading: true, blob: null, error: "" });
    try {
      const blob = await fetchFileContent(token, accessToken, file.id);
      setPreview({ document, loading: false, blob, error: "" });
    } catch (err) {
      if (isSessionRejected(err)) {
        setPreview(null);
        dropSession("Your session expired. Please verify with a new code.");
      } else {
        setPreview({ document, loading: false, blob: null, error: portalErrorMessage(err) });
      }
    }
  };

  const askRemove = (item, file) =>
    setConfirm({
      title: "Remove this file?",
      message: `"${file.filename}" will be removed from ${item.label}. You can upload a different file afterwards.`,
      confirmLabel: "Remove",
      confirmColor: "error",
      run: async () => {
        await runSessionAction(() => removeFile(token, accessToken, file.id));
        setConfirm(null);
      },
    });

  const missingRequired = useMemo(
    () =>
      (context?.items || [])
        .filter((item) => item.required && (item.files || []).length === 0)
        .map((item) => item.label),
    [context]
  );

  const askSubmit = () =>
    setConfirm({
      title: "Submit your documents?",
      message:
        "This sends everything you have uploaded to your consultant and closes this upload link. You will not be able to add or change files afterwards.",
      confirmLabel: "Submit documents",
      confirmColor: "primary",
      run: async () => {
        setConfirm(null);
        await handleSubmit();
      },
    });

  const handleSubmit = async () => {
    setBusy(true);
    try {
      await submitRequest(token, accessToken);
      setSubmitted(true);
      storeSession("");
    } catch (err) {
      if (isSessionRejected(err)) {
        dropSession("Your session expired. Please verify with a new code.");
      } else if (isDeadLink(err)) {
        // Submit's only 404 is the request already being closed.
        setDeadLink(true);
      } else {
        toast.error(portalErrorMessage(err));
      }
    } finally {
      setBusy(false);
    }
  };

  const shell = (children) => (
    <Container maxWidth="sm" className="py-10">
      {children}
    </Container>
  );

  if (submitted) {
    return shell(
      <Card>
        <CardContent>
          <Stack spacing={1}>
            <Typography variant="h5">Documents submitted</Typography>
            <Typography variant="body2" color="text.secondary">
              Thank you. Your documents have been sent and this link is now closed. Contact your
              consultant if you need to send anything else.
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return shell(<Typography variant="body2">Loading...</Typography>);
  }

  if (deadLink) {
    return shell(
      <Card>
        <CardContent>
          <Stack spacing={1}>
            <Typography variant="h5">This link is no longer available</Typography>
            <Typography variant="body2" color="text.secondary">
              It may have already been used, or it may have been withdrawn. Contact your consultant
              if you still need to send documents.
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  if (!accessToken || !context) {
    return shell(
      <OtpGate
        title={landing?.title}
        organizationName={landing?.organizationName}
        cooldownSeconds={cooldownSeconds}
        codeRequested={codeRequested}
        sending={sending}
        verifying={verifying}
        error={gateError}
        onSendCode={handleSendCode}
        onVerify={handleVerify}
      />
    );
  }

  return shell(
    <Stack spacing={2}>
      <Box>
        <Typography variant="h5">{context.title}</Typography>
        <Typography variant="body2" color="text.secondary">
          Upload each requested file below, then submit. You can come back to this link until you
          submit.
        </Typography>
      </Box>

      {(context.items || []).map((item) => (
        <UploadItemCard
          key={item.id}
          item={item}
          busy={busy}
          onUpload={handleUpload}
          onRemove={askRemove}
          onPreview={handlePreview}
        />
      ))}

      {missingRequired.length > 0 && (
        <Alert severity="info">
          Still needed: {missingRequired.join(", ")}
        </Alert>
      )}

      <Button
        variant="contained"
        size="large"
        disabled={busy || missingRequired.length > 0}
        onClick={askSubmit}
      >
        Submit documents
      </Button>
      <Typography variant="caption" color="text.secondary">
        Submitting closes this link. Make sure everything is uploaded first.
      </Typography>

      <DocumentPreviewDialog
        open={Boolean(preview)}
        document={preview?.document}
        loading={preview?.loading}
        error={preview?.error}
        blob={preview?.blob}
        onClose={() => setPreview(null)}
        onDownload={() => preview?.blob && saveBlob(preview.blob, preview.document.originalFilename)}
      />

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        confirmColor={confirm?.confirmColor}
        loading={busy}
        onConfirm={() => confirm?.run()}
        onCancel={() => setConfirm(null)}
      />
    </Stack>
  );
};

export default DocumentUploadPortal;
